<template>
  <v-card class="qa-card" variant="flat">
    <v-card-title class="d-flex align-center py-3">
      <v-icon color="primary" size="18" class="mr-2">mdi-chart-line-variant</v-icon>
      <span class="text-subtitle-1">{{ $t('qaSentiment') }}</span>
      <v-spacer />
      <span class="text-caption text-medium-emphasis d-none d-sm-inline">อารมณ์ตั้งแต่เริ่มจนจบการสนทนา · สเกล −1.00 ถึง +1.00</span>
    </v-card-title>
    <v-divider />
    <div class="pa-4">
      <div v-for="row in rows" :key="row.who" class="qa-sent-row mb-3" :class="`qa-sent-${row.endEmotion.valence}`">
        <div class="d-flex align-center flex-wrap ga-3">
          <v-avatar :color="faceColor(row.endEmotion.valence)" size="38" variant="tonal">
            <v-icon :color="faceColor(row.endEmotion.valence)">{{ faceIcon(row.party.end, row.who) }}</v-icon>
          </v-avatar>

          <div style="min-width: 190px">
            <div class="text-caption text-medium-emphasis">{{ row.label }}</div>
            <div class="d-flex align-center ga-1 flex-wrap">
              <v-chip size="x-small" variant="tonal" :color="faceColor(row.startEmotion.valence)" label>{{ row.startEmotion.th }}</v-chip>
              <v-icon size="14" color="grey">mdi-arrow-right</v-icon>
              <v-chip size="x-small" variant="tonal" :color="faceColor(row.endEmotion.valence)" label>{{ row.endEmotion.th }}</v-chip>
            </div>
          </div>

          <div class="d-flex ga-5">
            <div class="text-center">
              <div class="text-body-1 font-weight-medium qa-num" :class="polClass(row.party.start)">{{ toPolarity(row.party.start) }}</div>
              <div class="text-caption text-medium-emphasis">เริ่มต้น</div>
            </div>
            <div class="text-center">
              <div class="text-body-1 font-weight-medium qa-num" :class="polClass(row.party.end)">{{ toPolarity(row.party.end) }}</div>
              <div class="text-caption text-medium-emphasis">ตอนจบ</div>
            </div>
          </div>

          <div class="qa-sent-bars d-flex align-end ga-1">
            <div v-for="(v, i) in row.party.series" :key="i" class="qa-sent-bar" :style="barStyle(v, i, row.party.series.length, row.who)" />
          </div>

          <v-spacer />

          <div class="text-right">
            <div class="text-body-2 font-weight-medium" :class="`text-${row.shift.color}`">
              <v-icon size="16">{{ row.shift.icon }}</v-icon>
              {{ row.delta >= 0 ? '+' : '' }}{{ row.delta }}
            </div>
            <div class="text-caption text-medium-emphasis">{{ row.shift.label }}</div>
          </div>
        </div>
      </div>

      <div class="text-body-2 text-medium-emphasis mt-3">{{ narrative }}</div>
    </div>
  </v-card>
</template>

<script>
import { toPolarity, emotionOf, sentimentShift } from '@/utils/qaFormat';

export default {
  name: 'QaSentimentPanel',
  props: {
    sentiment: { type: Object, required: true },
    channel: { type: String, default: 'voice' },
  },
  computed: {
    rows() {
      return [
        { who: 'customer', label: this.$t('qaCustomer'), party: this.sentiment.customer },
        { who: 'agent', label: this.$t('qaAgent'), party: this.sentiment.agent },
      ].map((row) => ({
        ...row,
        startEmotion: emotionOf(row.party.start, row.who),
        endEmotion: emotionOf(row.party.end, row.who),
        delta: row.party.end - row.party.start,
        shift: sentimentShift(row.party.end - row.party.start),
      }));
    },
    narrative() {
      const c = this.sentiment.customer;
      const delta = c.end - c.start;
      const mood = delta >= 15 ? 'ถือว่าพลิกกลับมาได้ดีมาก' : delta >= 5 ? 'ดีขึ้นเล็กน้อย' : delta > -5 ? 'แทบไม่เปลี่ยน' : 'อารมณ์แย่ลงระหว่างสนทนา';
      const channelText = this.channel === 'voice' ? 'สายนี้' : 'แชทนี้';
      return `ลูกค้าเริ่มต้นด้วยอารมณ์ "${emotionOf(c.start, 'customer').th}" (${toPolarity(c.start)}) และจบที่ "${emotionOf(c.end, 'customer').th}" (${toPolarity(c.end)}) — ${mood} ส่วนเจ้าหน้าที่อยู่ในสภาพ "${emotionOf(this.sentiment.agent.end, 'agent').th}" ตลอด${channelText}`;
    },
  },
  methods: {
    toPolarity,
    faceColor(valence) {
      return { pos: 'success', neg: 'error', neutral: 'brown' }[valence] || 'grey';
    },
    faceIcon(score, who) {
      const v = emotionOf(score, who).valence;
      if (v === 'pos') return 'mdi-emoticon-happy-outline';
      if (v === 'neg') return 'mdi-emoticon-sad-outline';
      return 'mdi-emoticon-neutral-outline';
    },
    polClass(score) {
      if (score >= 55) return 'text-success';
      if (score <= 45) return 'text-error';
      return '';
    },
    barStyle(value, index, length, who) {
      const valence = emotionOf(value, who).valence;
      const color = { pos: '#2E7D32', neg: '#C62828', neutral: '#8D6E63' }[valence] || '#9e9e9e';
      return {
        height: `${4 + (value / 100) * 24}px`,
        background: color,
        opacity: (0.5 + 0.5 * (index / (length - 1))).toFixed(2),
      };
    },
  },
};
</script>

<style scoped>
.qa-sent-row {
  border-radius: 14px;
  padding: 12px 14px;
  border: 1px solid #eef0f2;
}
.qa-sent-pos {
  background: #f3f9f4;
}
.qa-sent-neg {
  background: #fdf3f3;
}
.qa-sent-neutral {
  background: #faf7f4;
}
.qa-sent-bars {
  height: 30px;
}
.qa-sent-bar {
  width: 6px;
  border-radius: 2px;
}
</style>
